import Navbar from "@/components/Common/Navbar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  CHANGELOGS,
  WORK_IN_PROGRESS,
  type ChangeItem,
  type ChangeType,
  type VersionStatus,
  type WipItem,
} from "../constants/changelogs";
import { cn } from "@/lib/utils";

const STATUS_BADGE: Record<
  VersionStatus,
  { label: string; className: string }
> = {
  preview: {
    label: "Preview",
    className:
      "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50",
  },
  beta: {
    label: "Beta",
    className:
      "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50",
  },
  "coming-soon": {
    label: "Coming Soon",
    className:
      "bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-50",
  },
};

const CHANGE_TYPE_BADGE: Record<
  ChangeType,
  { label: string; className: string }
> = {
  new: {
    label: "New",
    className:
      "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50",
  },
  improved: {
    label: "Improved",
    className: "bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50",
  },
  fixed: {
    label: "Fixed",
    className:
      "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-50",
  },
  deprecated: {
    label: "Deprecated",
    className:
      "bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-zinc-100",
  },
};

// ─── Sub-components ──────────────────────────────────────────

const ChangeRow = ({ item }: { item: ChangeItem }) => {
  const badge = CHANGE_TYPE_BADGE[item.type];
  return (
    <div className="flex gap-3 py-2.5">
      <div className="mt-0.5 w-[80px] flex-shrink-0">
        <Badge
          variant="outline"
          className={cn(
            "text-[11px] font-medium tracking-wide",
            badge.className,
          )}
        >
          {badge.label}
        </Badge>
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-zinc-800">{item.title}</p>
        <p className="text-sm leading-relaxed text-zinc-500">
          {item.description}
        </p>
        {item.devNote && (
          <p className="mt-1 rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-zinc-400">
            🛠 {item.devNote}
          </p>
        )}
      </div>
    </div>
  );
};

const WipRow = ({ item }: { item: WipItem }) => (
  <div className="flex gap-3 py-2.5">
    <div className="mt-0.5 w-[80px] flex-shrink-0">
      {/* Pulsing dot to signal active work */}
      <span className="flex items-center gap-1.5 pt-0.5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
        </span>
        <span className="text-[11px] font-medium text-amber-600">
          In Progress
        </span>
      </span>
    </div>
    <div className="flex flex-col gap-0.5">
      <p className="text-sm font-semibold text-zinc-800">{item.title}</p>
      <p className="text-sm leading-relaxed text-zinc-500">
        {item.description}
      </p>
      {item.devNote && (
        <p className="mt-1 rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-zinc-400">
          🛠 {item.devNote}
        </p>
      )}
    </div>
  </div>
);

const ChangelogContainer = () => {
  return (
    <div className="h-screen p-3 pr-0 pb-3">
      <div
        className="scrollbar-thin relative h-full overflow-y-auto"
        style={{
          transform: "translateZ(0)",
          scrollBehavior: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div className="flex min-h-full gap-4">
          {/* Sidebar */}
          <div className="sticky top-0 h-fit w-[300px] flex-shrink-0 px-3">
            <Navbar />
          </div>

          {/* Main content */}
          <div className="flex flex-1 justify-center md:ml-0 xl:-ml-[200px] 2xl:-ml-[300px]">
            <Card className="h-auto min-h-full w-[680px] bg-white pr-2">
              <div className="flex flex-col px-[32px] pt-5 pb-8">
                <h1 className="pt-5 text-xl leading-[36px] font-bold -tracking-wide text-zinc-900">
                  Changelog
                </h1>
                <p className="pb-6 text-xs text-zinc-400">
                  Changes prior to February 26, 2026 may not be fully reflected
                  here. This page tracks updates from that date forward.
                </p>

                <div className="flex flex-col">
                  {/* ── Work in Progress ── */}
                  {WORK_IN_PROGRESS.length > 0 && (
                    <div
                      className="animate-in fade-in slide-in-from-right-4 mb-8 duration-500 ease-in-out"
                      style={{
                        animationFillMode: "both",
                        animationDelay: "0ms",
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center self-stretch">
                          <div className="relative mt-1.5 flex h-2.5 w-2.5 flex-shrink-0 items-center justify-center">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-60" />
                            <span className="relative h-2.5 w-2.5 rounded-full bg-amber-400" />
                          </div>
                          <div className="mt-1 w-px flex-1 bg-zinc-100" />
                        </div>

                        <div className="flex-1 pb-0">
                          <div className="flex flex-wrap items-center gap-2 pb-1">
                            <span className="text-base font-bold text-zinc-800">
                              Currently in Development
                            </span>
                            <Badge
                              variant="outline"
                              className="border border-amber-200 bg-amber-50 text-[11px] font-medium text-amber-700 hover:bg-amber-50"
                            >
                              In Progress
                            </Badge>
                          </div>
                          <p className="mb-3 text-sm text-zinc-400">
                            These features are actively being worked on and will
                            ship in an upcoming release.
                          </p>
                          <div className="divide-y divide-zinc-50 rounded-lg border border-zinc-100 bg-zinc-50/40 px-3">
                            {WORK_IN_PROGRESS.map((item, i) => (
                              <WipRow key={i} item={item} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Version Entries ── */}
                  {CHANGELOGS.map((entry, i) => (
                    <div
                      key={entry.version}
                      className="animate-in fade-in slide-in-from-right-4 duration-500 ease-in-out"
                      style={{
                        animationFillMode: "both",
                        animationDelay: `${(i + 1) * 60}ms`,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center self-stretch">
                          <div className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full border-2 border-zinc-300 bg-white" />
                          {i < CHANGELOGS.length - 1 && (
                            <div className="mt-1 w-px flex-1 bg-zinc-100" />
                          )}
                        </div>

                        <div className="flex-1 pb-8">
                          <div className="flex flex-wrap items-center gap-2 pb-1">
                            <span className="text-base font-bold text-zinc-800">
                              v{entry.version}
                            </span>
                            {entry.status && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[11px] font-medium",
                                  STATUS_BADGE[entry.status].className,
                                )}
                              >
                                {STATUS_BADGE[entry.status].label}
                              </Badge>
                            )}
                            <span className="ml-auto text-xs text-zinc-400">
                              {new Date(entry.date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          </div>

                          <p className="mb-3 text-sm text-zinc-400">
                            {entry.summary}
                          </p>

                          <div className="divide-y divide-zinc-50 rounded-lg border border-zinc-100 bg-zinc-50/40 px-3">
                            {entry.changes.map((item, j) => (
                              <ChangeRow key={j} item={item} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangelogContainer;
